import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/client";
import { downloadBlob } from "@/utils/download";
import type {
  DocumentActivityEntry,
  EmployeeDocument,
  EmployeeDocumentType,
  EmployeeESignature,
  EmployeeESignatureWrite,
  EmployeeExamination,
} from "../types.employee_docs";

// ─── Query keys ─────────────────────────────────────────────────────────────

const EXAM_KEY = (userId: string) => ["employee-examination", userId] as const;
const DOCS_KEY = (userId: string) => ["employee-documents", userId] as const;
const ACTIVITY_KEY = (userId: string) => ["employee-doc-activity", userId] as const;
const TYPES_KEY = () => ["employee-document-types"] as const;

// ─── Examination ─────────────────────────────────────────────────────────────

export function useEmployeeExamination(userId: string) {
  return useQuery({
    queryKey: EXAM_KEY(userId),
    queryFn: async () => {
      const resp = await apiClient.get<{ data: EmployeeExamination }>(
        `/employee-examinations/${userId}/`
      );
      return resp.data.data;
    },
    enabled: !!userId,
  });
}

export function useUpdateEmployeeExamination(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      bhp_valid_until?: string | null;
      medical_valid_until?: string | null;
      notes?: string;
    }) => {
      const resp = await apiClient.patch<{ data: EmployeeExamination }>(
        `/employee-examinations/${userId}/`,
        payload
      );
      return resp.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: EXAM_KEY(userId) });
    },
  });
}

// ─── Documents ───────────────────────────────────────────────────────────────

export function useEmployeeDocuments(userId: string) {
  return useQuery({
    queryKey: DOCS_KEY(userId),
    queryFn: async () => {
      const resp = await apiClient.get<{ data: EmployeeDocument[] }>(
        `/employees/${userId}/documents/`
      );
      return resp.data.data;
    },
    enabled: !!userId,
  });
}

export function useCreateEmployeeDocument(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<EmployeeDocument> & { document_type: string }) => {
      const resp = await apiClient.post<{ data: EmployeeDocument }>(
        `/employees/${userId}/documents/`,
        payload
      );
      return resp.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: DOCS_KEY(userId) });
      qc.invalidateQueries({ queryKey: ACTIVITY_KEY(userId) });
    },
  });
}

type DocumentWritePayload = {
  id: string;
  document_type?: string;
  location?: string;
  title?: string;
  date_signed?: string | null;
  effective_from?: string | null;
  effective_to?: string | null;
  file?: string | null;
  notes?: string;
};

export function useUpdateEmployeeDocument(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: DocumentWritePayload) => {
      const resp = await apiClient.patch<{ data: EmployeeDocument }>(
        `/employees/${userId}/documents/${id}/`,
        payload
      );
      return resp.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: DOCS_KEY(userId) });
      qc.invalidateQueries({ queryKey: ACTIVITY_KEY(userId) });
    },
  });
}

export function useDeleteEmployeeDocument(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (docId: string) => {
      await apiClient.delete(`/employees/${userId}/documents/${docId}/`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: DOCS_KEY(userId) });
      qc.invalidateQueries({ queryKey: ACTIVITY_KEY(userId) });
    },
  });
}

// ─── Activity ────────────────────────────────────────────────────────────────

export function useEmployeeDocumentActivity(userId: string, enabled = true) {
  return useQuery({
    queryKey: ACTIVITY_KEY(userId),
    queryFn: async () => {
      const resp = await apiClient.get<{ data: DocumentActivityEntry[] }>(
        `/employees/${userId}/documents/activity/`
      );
      return resp.data.data;
    },
    enabled: !!userId && enabled,
  });
}

// ─── Document types ──────────────────────────────────────────────────────────

export function useEmployeeDocumentTypes() {
  return useQuery({
    queryKey: TYPES_KEY(),
    queryFn: async () => {
      const resp = await apiClient.get<{ data: EmployeeDocumentType[] }>(
        "/employee-document-types/"
      );
      return resp.data.data;
    },
  });
}

export function useCreateEmployeeDocumentType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { name: string; sort_order?: number }) => {
      const resp = await apiClient.post<{ data: EmployeeDocumentType }>(
        "/employee-document-types/",
        payload
      );
      return resp.data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: TYPES_KEY() }),
  });
}

export function useUpdateEmployeeDocumentType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...payload
    }: Partial<EmployeeDocumentType> & { id: string }) => {
      const resp = await apiClient.patch<{ data: EmployeeDocumentType }>(
        `/employee-document-types/${id}/`,
        payload
      );
      return resp.data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: TYPES_KEY() }),
  });
}

// ─── Electronic signatures ──────────────────────────────────────────────────

const ESIG_KEY = (userId: string) => ["employee-esignatures", userId] as const;

export function useEmployeeESignatures(userId: string) {
  return useQuery({
    queryKey: ESIG_KEY(userId),
    queryFn: async () => {
      const resp = await apiClient.get<{ data: EmployeeESignature[] }>(
        `/employees/${userId}/esignatures/`
      );
      return resp.data.data;
    },
    enabled: !!userId,
  });
}

export function useCreateEmployeeESignature(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: EmployeeESignatureWrite & { provider: string }) => {
      const resp = await apiClient.post<{ data: EmployeeESignature }>(
        `/employees/${userId}/esignatures/`,
        payload
      );
      return resp.data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ESIG_KEY(userId) }),
  });
}

export function useUpdateEmployeeESignature(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: EmployeeESignatureWrite & { id: string }) => {
      const resp = await apiClient.patch<{ data: EmployeeESignature }>(
        `/employees/${userId}/esignatures/${id}/`,
        payload
      );
      return resp.data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ESIG_KEY(userId) }),
  });
}

export function useDeleteEmployeeESignature(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (sigId: string) => {
      await apiClient.delete(`/employees/${userId}/esignatures/${sigId}/`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ESIG_KEY(userId) }),
  });
}

export async function downloadESignaturesXlsx(): Promise<void> {
  const resp = await apiClient.get<Blob>("/employee-analytics/esignatures-export/", {
    responseType: "blob",
  });
  const blob = new Blob([resp.data], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const today = new Date().toISOString().slice(0, 10);
  downloadBlob(blob, `podpisy-elektroniczne-${today}.xlsx`);
}
